import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Loader2, Mail } from 'lucide-react';
import { fetchEmailTemplates, deleteEmailTemplate } from '@/lib/api';
import type { EmailTemplate } from '@/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmailTemplateEditor } from './EmailTemplateEditor';

// Inverted label colors per template category (colored text + border on dark tint).
const CATEGORY_BADGE_CLASSES: Record<string, string> = {
  general: 'border-white/20 bg-white/10 text-text-secondary',
  recruitment: 'border-blue-500/30 bg-blue-500/15 text-blue-400',
  onboarding: 'border-green-500/30 bg-green-500/15 text-green-400',
  rejection: 'border-red-500/30 bg-red-500/15 text-red-400',
};

export function EmailTemplatesManager() {
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | undefined>();
  const [showEditor, setShowEditor] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: fetchEmailTemplates,
  });

  const handleCreate = () => {
    setEditingTemplate(undefined);
    setShowEditor(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setShowEditor(true);
  };

  const handleSave = () => {
    setShowEditor(false);
    setEditingTemplate(undefined);
    queryClient.invalidateQueries({ queryKey: ['email-templates'] });
  };

  const handleCancel = () => {
    setShowEditor(false);
    setEditingTemplate(undefined);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteEmailTemplate(deleteId);
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  if (showEditor) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</CardTitle>
        </CardHeader>
        <CardContent>
          <EmailTemplateEditor
            template={editingTemplate}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Email Templates</h3>
          <p className="text-sm text-text-secondary">Create and manage reusable email templates</p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Template
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Mail className="h-12 w-12 text-text-tertiary mb-4" />
            <p className="text-text-secondary">No email templates yet</p>
            <Button variant="outline" className="mt-4" onClick={handleCreate}>
              Create your first template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {templates.map((template) => (
            <Card key={template.id}>
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-text-primary">{template.name}</p>
                    {template.category && (
                      <Badge
                        variant="outline"
                        className={`text-xs capitalize ${
                          CATEGORY_BADGE_CLASSES[template.category] ?? ''
                        }`}
                      >
                        {template.category}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-text-tertiary mt-0.5 truncate">
                    Subject: {template.subject}
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(template)}>
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-500 hover:text-red-400"
                    onClick={() => setDeleteId(template.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this template? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
